import React from 'react';
import { fetchWrapper } from '../../APIManager';
import { TYPE_IDS_TO_EXCLUDE, FILTERS_LIST } from '../../components/Constants';
const OverviewStateContext = /*#__PURE__*/React.createContext();
const OverviewDispatchContext = /*#__PURE__*/React.createContext();
const initialState = {
  notifications: {
    activity: [],
    message: []
  },
  unreadActivityCount: 0,
  unreadMessagesCount: 0,
  messages: []
};

/** REDUCER **/
function OverviewReducer(state, action) {
  switch (action.type) {
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: {
          ...state.notifications,
          ...action.payload
        }
      };
    case 'SET_UNREAD_ACTIVITY_COUNT':
      return {
        ...state,
        unreadActivityCount: action.payload
      };
    case 'SET_MESSAGES':
      const mergedMessageData = action.payload && mergeMessageNotificationData({
        messages: action.payload,
        notifications: state.notifications
      });
      return {
        ...state,
        messages: mergedMessageData
      };
    case 'SET_UNREAD_MESSAGES_COUNT':
      return {
        ...state,
        unreadMessagesCount: action.payload
      };
    case 'GET_INITIAL':
      return initialState;
    default:
      {
        throw new Error('Unhandled action type caught');
      }
  }
}
function mergeMessageNotificationData({
  messages,
  notifications
}) {
  // add the read data from the notifications to the messages
  let mergedMessageData = [...messages]; // avoid pointer issues
  mergedMessageData = mergedMessageData.map(m => {
    // confusing, but yes, let's ensure the notification_id is the "root id" on this object
    return !m.topic_id ? {
      ...m,
      topic_id: m.id
    } : m;
  });
  const readMessageData = notifications.message;
  return mergedMessageData.map(m => {
    const thisTopicNotifications = readMessageData.filter(d => m.topic_id === d.topic_id);
    const anyUnread = thisTopicNotifications.some(d => !d.read);
    return {
      ...m,
      read: true,
      // id here replaces the topic_id of the message, which has been shifted to the attribute topic_id (see above)
      ...(thisTopicNotifications.length && {
        id: thisTopicNotifications[0].id,
        read: !anyUnread
      })
    };
  });
}

/** PROVIDER **/
function OverviewProvider({
  value,
  children
}) {
  const [state, dispatch] = React.useReducer(OverviewReducer, initialState);
  return /*#__PURE__*/React.createElement(OverviewStateContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(OverviewDispatchContext.Provider, {
    value: dispatch
  }, children));
}

/** STATE/DISPATCH HOOKS **/
function useOverviewState() {
  const context = React.useContext(OverviewStateContext);
  if (context === undefined) {
    throw new Error('useOverviewState must be used within a OverviewProvider');
  }
  return context;
}
function useOverviewDispatch() {
  const context = React.useContext(OverviewDispatchContext);
  if (context === undefined) {
    throw new Error('useOverviewDispatch must be used within a OverviewProvider');
  }
  return context;
}

/** API **/
const overviewAPI = {
  /*
    also possible to load more notifications
    by using response.load_more_notifications
    no need for now
  */
  getNotifications: async function (url, authUserName, authUserAPIKey, filter) {
    try {
      if (!FILTERS_LIST.includes(filter)) {
        throw new Error('Invalid filter provided');
      }
      const slug = `/notifications?username=${authUserName}&filter=${filter}`;
      const response = await fetchWrapper(url, slug, authUserAPIKey);
      if (!response || !response.notifications || !response.notifications.length) {
        throw new Error('Could not get notifications data.');
      }

      // let the caller deal with the various types of notifications here, just return it
      return response.notifications;
    } catch (err) {
      return [];
    }
  },
  /* Admins should have complete access to these endpoints
   * Moderators should only be able to see these DMs if they are the recipient, OR
   * if they have been flagged. */
  getPrivateMessages: async function (url, authUserName, authUserAPIKey) {
    try {
      const slug = `/topics/private-messages/${authUserName}.json`;
      const response = await fetchWrapper(url, slug, authUserAPIKey);
      if (!response) {
        throw new Error('Could not get private messages.');
      }
      return response;
    } catch (err) {
      return [];
    }
  },
  getPrivateMessagesSent: async function (url, authUserName, authUserAPIKey) {
    try {
      const slug = `/topics/private-messages-sent/${authUserName}.json`;
      const response = await fetchWrapper(url, slug, authUserAPIKey);
      if (!response) {
        throw new Error('Could not get private messages sent.');
      }
      return response;
    } catch (err) {
      return [];
    }
  },
  getBadge: async function (url, apiKey, badgeId) {
    try {
      const slug = `/badges/${badgeId}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get badge.');
      }
      return response;
    } catch (err) {
      return false;
    }
  },
  setActivityToRead: async function (url, apiKey, activityId) {
    try {
      const slug = '/notifications/mark-read.json';
      const data = {
        id: activityId
      };
      const res = await fetchWrapper(url, slug, apiKey, 'PUT', data);
      if (!res || res.success !== 'OK') {
        throw new Error('Failed to mark notification as read.');
      }
      return true;
    } catch (err) {
      return false;
    }
  }
};
export { OverviewProvider, useOverviewState, useOverviewDispatch, overviewAPI };
//# sourceMappingURL=index.js.map